import React from "react";
import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import { TopLogo, BottomLogo } from "./Logo";
import { color } from "../../../atoms/colors";

const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    paddingTop: 20,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 0,
    fontFamily: 'Helvetica'
  },
  padding: {
    padding: 16,
  },
  margin: {
    marginBottom: 16,
  },
  subSection: {
    backgroundColor: `${color.common.lightBlue}`,
    borderRadius: 8,
    rowGap: 8,
  },
  title: {
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 900,
    fontFamily: 'Helvetica-Bold',
  },
  text: {
    fontSize: 10,
    lineHeight: 1.3,
    flexGrow: 0,
  },
  row: {
    flexDirection: 'row',
    columnGap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: `0.5px solid ${color.grey[200]}`
  },
  bold: {
    fontSize: 10,
    lineHeight: 1.3,
    fontWeight: 900,
    fontFamily: 'Helvetica-Bold',
  },
  blue: {
    color: `${color.common.workgridBlue}`,
  },
  grey: {
    color: `${color.grey[600]}`,
  },
  footer: {
    fontSize: 8,
    position: 'absolute',
    bottom: 12,
    left: 20,
  }
});

const ResultsDocument = ({ 
  annualApprovalsSavings, 
  annualProductivitySavings,
  annualItSavings,
  total,
  prodHoursSaved,
  ticketsPerEmp,
  employees
}) => (
  <Document>
    <Page size="letter" style={styles.page}>
      <View>
        <TopLogo />
        <Text style={[styles.title, styles.margin, {paddingLeft: 16}]}>Workgrid ROI Results</Text>
        <View style={[styles.subSection, styles.padding, styles.margin]}>
          <Text style={[styles.title]}>Streamlining Approvals</Text>
          <Text style={[styles.title, styles.blue]}>{annualApprovalsSavings} Saved Per Year</Text>
          <Text style={styles.text}>Workgrid dramatically reduces the amount of time to complete approvals. From timesheet approvals to expense reports, Workgrid saves employees a minimum of 45 seconds per approval.</Text>
          <Text style={[styles.text, styles.bold]}>How This is Calculated</Text>
          <Text style={styles.text}>This number was calculated by taking the average number of approvals for an organization of your size and multiplying it by the average time saved from customer data.</Text>
        </View>
        <View style={[styles.subSection, styles.padding, styles.margin]}>
          <Text style={[styles.title]}>Reduced Time to View Information</Text>
          <Text style={[styles.title, styles.blue]}>{annualProductivitySavings} Saved Per Year</Text>
          <Text style={[styles.title, styles.blue]}>{prodHoursSaved} Hours Saved Per Week</Text>
          <Text style={styles.text}>On average, Workgrid reduces the time to find information by 60% by instantly surfacing data from third-party systems and knowledge sources. From HR policies to time off balances, employees can find it all through the Workgrid AI Assistant.</Text>
          <Text style={styles.text}>On average, for a company your size, you’d save employees a minimum of 5 minutes per week with just a few use cases. Imagine the possibilities if you had  had various use cases implemented across multiple departments!</Text>
          <Text style={[styles.text, styles.bold]}>How This is Calculated</Text>
          <Text style={styles.text}>This number is calculated by taking the average number of minutes per week saved per employee (based on customer data) and multiplying it by the total number of employees at your company.</Text>
        </View>
        <View style={[styles.subSection, styles.padding, styles.margin]}>
          <Text style={[styles.title]}>Reduction in Time to Submit Tickets</Text>
          <Text style={[styles.title, styles.blue]}>93% Reduction in Time to Submit Tickets</Text>
          <Text style={[styles.title, styles.blue]}>{annualItSavings} Cost Savings</Text>
          <Text style={styles.text}>On average, customers using the Workgrid digital assistant to submit tickets (e.g. Help Desk Tickets), see a 93% reduction in the handling time of tickets. This correlates to a significant cost savings for your IT department and a significant reduction in friction for your employees.</Text>
          <Text style={[styles.text, styles.bold]}>How This is Calculated</Text>
          <Text style={styles.text}>The number is calculated by taking the average handling time saved, per ticket and multiplying it by the number of tickets handled at your organization, we then multiply that by the average handling cost per ticket.</Text>
          <Text style={styles.text}>Based on a company of your size, we used an estimated number of tickets handled per year. <Link src="https://www.thinkhdi.com/~/media/HDICorp/Files/Library-Archive/Insider%20Articles/cost-per-ticket-per-user.pdf">Research shows {ticketsPerEmp} tickets are created per month per employee.</Link> According to Gartner, the average cost per IT Support ticket ranges from, $15-$35/ticket.</Text>
        </View>
        <Text style={[styles.footer, styles.grey]}>Page 1 of 2</Text>
      </View>
      <BottomLogo />
    </Page>
    <Page size="letter" style={styles.page}>
      <View style={{ width: '100%'}}>
        <TopLogo />
        <View style={[styles.padding, {marginTop:-16}]}>
          <Text style={styles.title}>ROI Calculation Results</Text>
          <Text style={[styles.text, styles.margin]}>Annualized Savings</Text>
          <View>
            <View style={styles.row}>
              <Text style={[styles.grey, styles.text]}>Number of employees in your organization</Text>
              <Text style={styles.title}>{employees}</Text>
            </View>
            <View style={styles.row}>
              <View>
                <Text style={[styles.grey, styles.text]}>Streamlining Approvals</Text>
                <Text style={[styles.grey, styles.text]}>Employee Productivity Savings</Text>
              </View>
              <Text style={styles.title}>{annualApprovalsSavings}</Text>
            </View>
            <View style={styles.row}>
              <View>
                <Text style={[styles.grey, styles.text]}>Minimize Time to Find Information</Text>
                <Text style={[styles.grey, styles.text]}>Employee Productivity Savings</Text>
              </View>
              <Text style={styles.title}>{annualProductivitySavings}</Text>
            </View>
            <View style={styles.row}>
              <Text style={[styles.grey, styles.text]}>IT/HR Support Cost Savings</Text>
              <Text style={styles.title}>{annualItSavings}</Text>
            </View>
          </View>
          <View style={[styles.row, {borderBottom: 'none'}]}>
            <Text style={[styles.title, styles.blue]}>Total Savings</Text>
            <Text style={[styles.title, styles.blue]}>{total}</Text>
          </View>
        </View>
        <Text style={[styles.footer, styles.grey]}>Page 2 of 2</Text>
      </View>
      <BottomLogo />
    </Page>
  </Document>
)

export default ResultsDocument
